<template>
    <div class="relative">
        <label class="relative block" ref="reference">
            <input
                class="h-10 w-full flex items-center gap-2 text-left pl-3 text-slate-500 dark:text-slate-400 relative bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 py-1.5 placeholder:text-slate-500 dark:text-slate-400 placeholder:text-sm transition-colors text-sm focus:ring-0 focus:border-slate-800 hover:border-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                v-model="programQuery"
                @focus="openDropdown"
                @click="openDropdown"
                ref="programsButton"
                :class="
                    tripFormErrors?.payment_program_id ? '!border-red-500' : ''
                "
            />
            <div
                class="w-full flex items-center gap-2 absolute pr-14 left-2 top-[50%] -translate-y-[calc(50%-0.125rem)] whitespace-nowrap truncate min-w-0"
            >
                <ImageLoader v-if="program">
                    <template #image>
                        <img
                            :src="`${cdnUrl}/${program?.program_image?.path}/_small/${program?.program_image?.file_name}`"
                            :alt="program?.name"
                            class="max-w-10 max-h-5 rounded-sm"
                        />
                    </template>
                    <template #error>
                        <Icon icon="points" class="text-slate-600 dark:text-slate-300 w-8 h-5" />
                    </template>
                </ImageLoader>
                <span class="truncate w-full overflow-hidden text-sm">
                    {{ program?.id ? program?.name : '' }}
                </span>
            </div>
            <button
                class="block text-red-600 w-5 h-5 absolute right-2 top-1/2 -translate-y-1/2 z-10"
                v-if="program"
                @click="clearProgram()"
            >
                <Icon icon="close" />
            </button>
        </label>
        <div
            class="text-red-500 text-xs flex items-center gap-2 py-1.5"
            role="alert"
            v-if="tripFormErrors?.payment_program_id"
        >
            <Icon icon="caution" />
            Please choose a points currency
        </div>

        <Teleport to="body">
            <transition name="fade">
                <div
                    class="max-h-48 p-2 overflow-y-auto bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 fixed z-[99999]"
                    v-show="showList"
                    ref="floating"
                    :style="`width: ${width}px; top: ${top}px; left: ${left}px`"
                >
                    <label
                        class="w-full flex items-center gap-2 px-3 py-2 text-slate-800 dark:text-slate-100 text-sm bg-white dark:bg-slate-800 rounded hover:bg-slate-100 dark:hover:bg-slate-700 dark:bg-slate-700 transition-colors mb-1 cursor-pointer"
                        v-for="program in programsFiltered"
                        :key="program.id"
                        @click="
                            showList = false;
                            programQuery = null;
                        "
                    >
                        <input
                            class="sr-only"
                            type="radio"
                            name="connected_account_id"
                            :value="program.id"
                            v-model="activeRedemption.payment_program_id"
                        />
                        <div class="w-12 flex justify-center">
                            <ImageLoader>
                                <template #image>
                                    <img
                                        :src="`${cdnUrl}/${program.image?.path}/_small/${program.image?.file_name}`"
                                        :alt="program?.label"
                                        class="max-w-10 max-h-5 rounded-sm"
                                    />
                                </template>
                                <template #error>
                                    <Icon
                                        icon="points"
                                        class="text-slate-600 dark:text-slate-300 w-8 h-5"
                                    />
                                </template>
                            </ImageLoader>
                        </div>
                        {{ program?.label }}
                    </label>
                    <div
                        v-if="programsFiltered.length === 0"
                        class="px-3 py-2 text-sm text-slate-800 dark:text-slate-100"
                    >
                        No programs found
                    </div>
                </div>
            </transition>
        </Teleport>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import {
    onMounted,
    onBeforeUnmount,
    ref,
    useTemplateRef,
    watch,
    computed,
} from 'vue';
import { useTripsStore } from '@/store/trips';
import { autoUpdate } from '@floating-ui/vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';

const tripsStore = useTripsStore();
const { activeRedemption, redemptionTypes, programs, program, tripFormErrors } =
    storeToRefs(tripsStore);

const programQuery = ref(null);
const showList = ref(false);
const programsButton = useTemplateRef('programsButton');
const reference = ref(null);
const floating = ref(null);
const top = ref(0);
const left = ref(0);
const width = ref(80);
let stopAutoUpdate = null;

const openDropdown = () => {
    showList.value = true;
};

const setDropdownPosition = () => {
    if (!programsButton.value) {
        return;
    }
    const position = programsButton.value.getBoundingClientRect();
    top.value = position.top + 42;
    left.value = position.left;
    width.value = position.width;
};

// Teleported dropdown lives outside the trigger's DOM subtree, so a
// document-level v-click-outside on the input treats every touch or click
// inside the floating list as "outside" and closes before selection can
// register (SUPPORT-767). Mirror the transfer-programs-dropdown pattern:
// check both the trigger and the floating list, and use touchend so the
// initial touchstart of a scroll gesture doesn't dismiss the list.
const handleGlobalClose = (event) => {
    if (
        (floating.value && floating.value.contains(event.target)) ||
        (reference.value && reference.value.contains(event.target))
    ) {
        return;
    }

    showList.value = false;
};

const handleEscape = (event) => {
    if (event.key === 'Escape' && showList.value) {
        showList.value = false;
    }
};

// While open, keep the teleported list glued to the input across every
// possible cause of movement: modal scroll, page scroll, window resize,
// browser zoom, layout shifts, and input size changes. autoUpdate walks
// the reference's scroll ancestors — unlike a single hard-coded
// `.ui-modal-content` scroll listener, which can bind to the wrong
// hidden modal-content when several stacked modals exist in the DOM.
watch(showList, (val) => {
    if (val) {
        setDropdownPosition();
        if (programsButton.value && floating.value) {
            stopAutoUpdate = autoUpdate(
                programsButton.value,
                floating.value,
                setDropdownPosition
            );
        }
    } else if (stopAutoUpdate) {
        stopAutoUpdate();
        stopAutoUpdate = null;
    }
});

onMounted(() => {
    document.addEventListener('click', handleGlobalClose);
    document.addEventListener('touchend', handleGlobalClose);
    document.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
    if (stopAutoUpdate) {
        stopAutoUpdate();
        stopAutoUpdate = null;
    }
    document.removeEventListener('click', handleGlobalClose);
    document.removeEventListener('touchend', handleGlobalClose);
    document.removeEventListener('keydown', handleEscape);
});

const redemptionType = computed(() => {
    return redemptionTypes.value.find(
        (redemptionType) =>
            redemptionType.id === activeRedemption.value.redemption_type_id
    );
});

const programsFiltered = computed(() => {
    if (!programs.value?.length) return [];

    let programType = redemptionType.value?.slug;

    if (programType === 'flight') {
        programType = 'airline';
    }
    const prefilteredPrograms = programs.value
        .map((program) => {
            return {
                label: program.name,
                value: program.type,
                id: program.id,
                image: program.program_image,
            };
        })
        .filter(
            (program) =>
                program.value === programType ||
                program.value === 'flexible_currency' ||
                redemptionType.value?.slug === 'other' ||
                redemptionType.value?.slug === 'rental-car' ||
                redemptionType.value?.slug === 'train'
        );

    return prefilteredPrograms.filter((program) => {
        return programQuery.value && programQuery.value.length > 0
            ? program.label
                  .toLowerCase()
                  .replace('.', '')
                  .includes(programQuery.value.toLowerCase())
            : true;
    });
});

const clearProgram = () => {
    activeRedemption.value.payment_program_id = null;
};

const cdnUrl = computed(() => {
    return window.cdnUrl;
});
</script>
